import { format } from 'date-fns';
import { Device } from '@/hooks/useTriggerStream';

// Catmull-Rom -> bezier: continuous tangents, so the line flows instead of
// stepping between points.
export function smoothLine(points: Array<{ x: number; y: number }>): string {
  if (!points.length) return '';
  if (points.length < 3) return points.map((p, i) => `${i ? 'L' : 'M'}${p.x},${p.y}`).join(' ');
  let d = `M${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`;
  }
  return d;
}

// Time labels for an arbitrary [tMin, tMax] window — format adapts to the span.
export function windowTimeLabels(tMin: number, tMax: number, count = 6): string[] {
  const span = Math.max(1, tMax - tMin);
  const dayMs = 864e5;
  const fmt = span <= 2 * dayMs ? 'HH:mm' : span <= 90 * dayMs ? 'MMM dd' : 'MMM yyyy';
  return Array.from({ length: count }, (_, i) =>
    format(new Date(tMin + (span * i) / (count - 1)), fmt),
  );
}

// Resolves a device id to its configured name, falling back to a numbered
// "Light sculpture NN" label based on the sorted id list.
export function makeDeviceLabel(
  devices: Device[],
  sortedIds: string[],
  fallback: (n: string) => string,
): (deviceId: string) => string {
  return (deviceId: string) => {
    const name = devices.find((d) => d.device_id === deviceId)?.name?.trim();
    if (name) return name;
    const idx = sortedIds.indexOf(deviceId);
    return fallback(String(idx + 1).padStart(2, '0'));
  };
}
